import { assertNever } from '../../lib/exhaustive';
import type { MappingEntryReading } from '../../lib/rcu/link/readings';
import type { RcuSceneEntry } from '../../lib/rcu/wire';
import type { KeyState } from './catalog/keyState';
import { DEVICE_ANSWER, STATE_STATUS } from './catalog/status';
import type { DeviceAnswerKind, StateStatus } from './catalog/status';
import { entryFromReading, fingerprintOf, triggersOf } from './encode';
import type { EncodedKeypad, EncodedState, TriggerRef } from './encode';
import type { KeypadKeyId } from './ids';
import type { Keypad, KeypadBook, KeypadKey } from './model';

/**
 * What this tablet wrote, and what the device said it holds.
 *
 * Neither is authoritative. A write is only ever *delivered* — the firmware
 * acknowledges `rcuaddmapping` with a `mappingack` that carries nothing to match
 * it to a request — and a read is what the device held when it answered, out of a
 * buffer that can truncate a panel's worth of entries. The two are kept side by
 * side here so the console can say which of them it is showing.
 */

export interface DeviceAnswer {
  readonly kind: DeviceAnswerKind;
  /** The entry the device holds, when it could be read as a mapping. */
  readonly entry: RcuSceneEntry | null;
  /** The answer as it arrived, for the console to show verbatim. */
  readonly reading: MappingEntryReading | null;
}

/** What the device answered, keyed by the trigger it answers. */
export type DeviceAnswers = Readonly<Record<string, DeviceAnswer | undefined>>;

/** Nothing has been asked about this trigger since the link came up. */
export const unknownAnswer = (): DeviceAnswer => ({
  kind: DEVICE_ANSWER.UNKNOWN,
  entry: null,
  reading: null,
});

/** A read run has finished and the device holds nothing at this trigger. */
export const absentAnswer = (): DeviceAnswer => ({
  kind: DEVICE_ANSWER.ABSENT,
  entry: null,
  reading: null,
});

/** Turns one entry the device answered with into what the console knows. */
export const answerFrom = (reading: MappingEntryReading): DeviceAnswer => {
  const entry = entryFromReading(reading);
  return entry === null
    ? { kind: DEVICE_ANSWER.UNREADABLE, entry: null, reading }
    : { kind: DEVICE_ANSWER.MAPPING, entry, reading };
};

/** The answer for a trigger, or "not asked" when a run did not cover it. */
export const answerFor = (answers: DeviceAnswers, trigger: string): DeviceAnswer =>
  answers[trigger] ?? unknownAnswer();

/** How one key state stands against this tablet and against the device. */
export interface KeyStateReport {
  readonly key: KeypadKey;
  readonly state: KeyState;
  /** The trigger the device answers this key state on. */
  readonly trigger: string;
  /** The entry this console would write, or null when the state drives nothing. */
  readonly encoded: EncodedState | null;
  /** The fingerprint last written here, or null when nothing was. */
  readonly writtenFingerprint: string | null;
  readonly answer: DeviceAnswer;
  readonly status: StateStatus;
}

/**
 * What one key state's status is.
 *
 * "Written" only means delivered, and "matches" only means the device agreed the
 * last time it was asked, so both are read as claims about the past rather than
 * about the device right now.
 */
export const statusOf = (
  encodedFingerprint: string | null,
  writtenFingerprint: string | null,
  answer: DeviceAnswer,
): StateStatus => {
  switch (answer.kind) {
    case DEVICE_ANSWER.UNKNOWN:
      if (writtenFingerprint === null) return STATE_STATUS.UNWRITTEN;
      return encodedFingerprint === writtenFingerprint ? STATE_STATUS.WRITTEN : STATE_STATUS.STALE;
    case DEVICE_ANSWER.ABSENT:
      return encodedFingerprint === null ? STATE_STATUS.UNWRITTEN : STATE_STATUS.NOT_STORED;
    case DEVICE_ANSWER.UNREADABLE:
      return STATE_STATUS.UNREADABLE;
    case DEVICE_ANSWER.MAPPING:
      if (encodedFingerprint === null) return STATE_STATUS.DIFFERS;
      return answer.entry !== null && fingerprintOf(answer.entry) === encodedFingerprint
        ? STATE_STATUS.MATCHES
        : STATE_STATUS.DIFFERS;
    default:
      return assertNever(answer.kind);
  }
};

/**
 * Worst-first order for summarising a panel in a list.
 *
 * A device that contradicts this console is worth more attention than one that
 * has simply not been written yet, so the badge on a panel row is the most
 * interesting thing about it rather than the first or the last.
 */
const STATUS_PRECEDENCE = [
  STATE_STATUS.DIFFERS,
  STATE_STATUS.UNREADABLE,
  STATE_STATUS.STALE,
  STATE_STATUS.NOT_STORED,
  STATE_STATUS.UNWRITTEN,
  STATE_STATUS.WRITTEN,
  STATE_STATUS.MATCHES,
] as const satisfies readonly StateStatus[];

/**
 * The statuses worth a panel-level badge.
 *
 * A key state that drives nothing and that the device holds nothing for says
 * nothing about the panel: every panel has empty states — an unprogrammed
 * release half, an unused key — and reporting one of them as "not written" would
 * put a warning on a panel that is entirely in order. What is left is the states
 * this console writes and the states the device holds something for.
 */
const isSignificant = (report: KeyStateReport): boolean =>
  report.encoded !== null ||
  report.answer.kind === DEVICE_ANSWER.MAPPING ||
  report.answer.kind === DEVICE_ANSWER.UNREADABLE;

/** The most interesting status among a panel's key states, or null if none is. */
export const overallStatus = (reports: readonly KeyStateReport[]): StateStatus | null => {
  const significant = reports.filter(isSignificant);
  for (const status of STATUS_PRECEDENCE) {
    if (significant.some((report) => report.status === status)) return status;
  }
  return null;
};

/** Every key state of a panel, in key order, with its status. */
export const keyStateReports = (
  book: KeypadBook,
  keypad: Keypad,
  encoded: EncodedKeypad,
  answers: DeviceAnswers,
): readonly KeyStateReport[] => {
  const byTrigger = new Map(encoded.entries.map((entry) => [entry.triggerKey, entry]));
  return triggersOf(keypad).map((ref) => {
    const entry = byTrigger.get(ref.triggerKey) ?? null;
    const answer = answerFor(answers, ref.triggerKey);
    const writtenFingerprint = book.written[ref.triggerKey] ?? null;
    return {
      key: ref.key,
      state: ref.state,
      trigger: ref.triggerKey,
      encoded: entry,
      writtenFingerprint,
      answer,
      status: statusOf(entry?.fingerprint ?? null, writtenFingerprint, answer),
    };
  });
};

/**
 * The triggers worth erasing for a panel.
 *
 * Only what this tablet wrote and what the device answered with a mapping:
 * erasing anything else is a write to the device's NVS for no gain, and one of
 * those writes is a hundred milliseconds the firmware's queue may not have.
 * Both the panel's current address and any it was moved off are covered, so a
 * mapping left behind at an old address can be cleared from here.
 */
export const erasePlan = (
  book: KeypadBook,
  keypad: Keypad,
  answers: DeviceAnswers,
): readonly TriggerRef[] => {
  const addresses: readonly (number | null)[] = [keypad.address, ...keypad.retiredAddresses];
  const seen = new Set<string>();
  return addresses
    .flatMap((address) => triggersOf(keypad, address))
    .filter((ref) => {
      if (seen.has(ref.triggerKey)) return false;
      seen.add(ref.triggerKey);
      return (
        book.written[ref.triggerKey] !== undefined ||
        answerFor(answers, ref.triggerKey).kind === DEVICE_ANSWER.MAPPING
      );
    });
};

/** The report for one key state, when the key has one. */
export const reportFor = (
  reports: readonly KeyStateReport[],
  keyId: KeypadKeyId,
  state: KeyState,
): KeyStateReport | undefined =>
  reports.find((report) => report.key.id === keyId && report.state === state);

/** The book with one trigger's fingerprint remembered, or forgotten when empty. */
export const withWritten = (
  book: KeypadBook,
  trigger: string,
  fingerprint: string | null,
): KeypadBook => {
  const written = { ...book.written };
  if (fingerprint === null) delete written[trigger];
  else written[trigger] = fingerprint;
  return { ...book, written };
};

/** Forgets everything written at a set of triggers, e.g. a removed panel's. */
export const withoutWritten = (book: KeypadBook, triggers: readonly string[]): KeypadBook => {
  const written = { ...book.written };
  for (const trigger of triggers) delete written[trigger];
  return { ...book, written };
};

/** True when this tablet wrote something at that trigger. */
export const wasWritten = (book: KeypadBook, trigger: string): boolean =>
  book.written[trigger] !== undefined;

/**
 * The triggers this tablet wrote to and the panel no longer answers on: the ones
 * at its current address, at every address it was moved off, or at one address
 * the caller names — which is what "clean up the old address" erases.
 */
export const writtenTriggersOf = (
  book: KeypadBook,
  keypad: Keypad,
  addresses?: readonly (number | null)[],
): readonly string[] => {
  const look = addresses ?? [keypad.address, ...keypad.retiredAddresses];
  const triggers = new Set<string>();
  for (const address of look) {
    for (const ref of triggersOf(keypad, address)) {
      if (wasWritten(book, ref.triggerKey)) triggers.add(ref.triggerKey);
    }
  }
  return [...triggers];
};
